/// <amd-module name="Wit/Common/WorkItemControls/AreaPathDropdown" />

import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IReadonlyObservableArray, ObservableArray, ObservableLike, useObservableArray } from "VSS/Core/Observable";
import { useComponentContext } from "VSS/Platform/Layout";
import { EditableDropdown } from "VSSUI/EditableDropdown";
import { IListBoxItem, wrapListBoxItems } from "VSSUI/ListBox";
import { WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { INodeTreeDropdownProps, NodeTreeDropdown } from "Wit/Common/WorkItemControls/NodeTreeDropdown";
import { INodeTreeItem } from "Wit/Common/WorkItemControls/NodeTreeItemProvider";
import { convertNodesToListBoxItems, isEmpty, normalizePath } from "Wit/Common/WorkItemControls/NodeUtils";
import { IWorkItemNodeService } from "Wit/Common/WorkItemNodeService/IWorkItemNodeService";

export interface IAreaPathDropdownProps extends Omit<INodeTreeDropdownProps, "items"> {
    /**
     * Items should be supplied as an ObservableArray if they will be changing. Allows empty list box items so loading cell can be passed in.
     */
    items?: IReadonlyObservableArray<IListBoxItem<INodeTreeItem>> | IListBoxItem<INodeTreeItem>[];
    /**
     * List of macros that will be displayed at the top of the tree
     * NOTE: if an itemProvider is passed, this list will be ignored. Parent component should manage macros in that case.
     */
    macros?: string[] | IListBoxItem[];
    /**
     * Optional project id, will default to current project if iteration data needs to be loaded
     */
    projectId?: string;
    /**
     * On initial mount, select the default node if selection is empty
     */
    selectDefaultValue?: boolean;
}

export const AreaPathDropdown = React.forwardRef<EditableDropdown<INodeTreeItem>, IAreaPathDropdownProps>((props, ref) => {
    const { items, macros, onSetPath, onValueChange, projectId, selectDefaultValue, ...rest } = props;
    const { pageContext } = useComponentContext();
    const pageData = getTFSPageData(pageContext);
    const nodeRef = React.useRef<IListBoxItem<WorkItemClassificationNode>[]>([]);
    const wrappedMacros = macros && (wrapListBoxItems(macros) || macros);
    const wrappedItems = ObservableLike.isObservable(items)
        ? (items as ObservableArray<IListBoxItem<WorkItemClassificationNode>>)
        : new ObservableArray((items as IListBoxItem<WorkItemClassificationNode>[]) ?? wrappedMacros);

    const [mruNodes] = useObservableArray<number>([]);

    const wrappedItemsRef = React.useRef<ObservableArray<IListBoxItem<WorkItemClassificationNode>>>(new ObservableArray(wrappedItems.value.concat(nodeRef.current)));

    React.useEffect(() => {
        wrappedItemsRef.current.value = wrappedItems.value.concat(nodeRef.current);
    }, [wrappedItems.length])


    React.useEffect(() => {
        const loadAreaPaths = async () => {
            const projectToLoad = projectId ?? pageData?.project?.id;
            if (!items && projectToLoad) {
                const nodeService = pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");
                const node = await nodeService.getAreaPathNodes(projectToLoad);

                if (node) {
                    nodeRef.current = convertNodesToListBoxItems(node);
                    wrappedItemsRef.current.value = wrappedItems.value.concat(nodeRef.current);
                    if (selectDefaultValue) {
                        onSetPath?.(normalizePath(node.path));
                    }
                }
            }
        };

        const loadMruData = async () => {
            const projectToLoad = projectId ?? pageData?.project?.id;
            if (projectToLoad) {
                const nodeService = pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");
                mruNodes.value = await nodeService.getMruAreaPaths(projectToLoad);
            }
        };

        if (!items) {
            loadAreaPaths();
        } else if (selectDefaultValue && wrappedItems.length && wrappedItems.value[0].text) {
            onSetPath?.(wrappedItems.value[0].text);
        }

        loadMruData();
    }, [projectId]);

    return (
        <NodeTreeDropdown
            allowFreeform={!!macros}
            items={items ? wrappedItems : wrappedItemsRef.current}
            mruNodes={mruNodes}
            onValueChange={async value => {
                if (onValueChange) {
                    onValueChange(value);
                }

                onSetPath?.(value?.text || "");

                const project = projectId ?? pageData?.project?.id;
                if (!project || isEmpty(value?.data)) {
                    return;
                }

                const node = value!.data as WorkItemClassificationNode;
                const nodeService = pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");
                mruNodes.value = await nodeService.updateMruAreaPaths(project, [node.id]);
            }}
            ref={ref}
            {...rest}
        />
    );
});
